/**
 * Generic dashboard primitive types — shared across reusable components.
 * NOT dashboard-specific (e.g. WidgetKey lives in views/home/dashboard/types).
 */

/**
 * A status row reusable across StatusList, MetricTile color resolution, etc.
 * `color` is the raw "R,G,B" string from public.status.color_v2; the
 * formatRgb helper wraps it as "rgb(R,G,B)" for CSS.
 */
export interface StatusEntry {
  id: number;
  label: string;
  color: string | null;
  order: number | null;
}

/** Wraps an "R,G,B" string as `rgb(...)`. Returns fallback when null/blank. */
export function formatRgb(color: string | null | undefined, fallback = "rgb(156,163,175)"): string {
  if (!color || !color.trim()) return fallback;
  return `rgb(${color.trim()})`;
}

/** Cents → "$X,XXX.XX" with exact cents. Defaults to "$0.00" for null/undefined. */
export function formatCurrencyCents(cents: number | null | undefined): string {
  if (cents == null || Number.isNaN(cents)) return "$0.00";
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Cents → compact "$1.4M" / "$250K" / "$842" — for tight chart axes where
 * full numbers wouldn't fit. < $1K stays as the plain dollar amount.
 */
export function formatCurrencyCompact(cents: number | null | undefined): string {
  if (cents == null || Number.isNaN(cents)) return "$0";
  const dollars = cents / 100;
  const abs = Math.abs(dollars);
  if (abs >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `$${(dollars / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(dollars).toLocaleString("en-US")}`;
}
